import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PERMISSIONS } from '@lims/permissions';
import {
  ALL_SAMPLE_MOVEMENT_TYPES,
  MOVEMENTS_REQUIRING_NOTE,
  SAMPLE_MOVEMENT_TYPE,
} from '@lims/shared-types';
import { samplesApi } from '../../api/endpoints.js';
import type { Sample } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import { useLaboratoryTimeZone } from '../../app/laboratory-time-zone.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  DateTimeField,
  Dialog,
  Panel,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
  useToast,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { toneForMovementType } from './status.js';

/**
 * Where this sample has been, and who has held it.
 *
 * ===========================================================================
 * CUSTODY IS APPEND-ONLY. A CHAIN WITH AN EDITABLE LINK IS NOT A CHAIN (D75).
 * ===========================================================================
 *
 * There is no control here that edits or deletes a movement, and there is no
 * endpoint behind one: the database refuses both. A mistake is fixed by
 * recording a `correction`, which states what actually happened and leaves the
 * original visible. That is the entire point — a record that could be quietly
 * rewritten proves nothing about where the material was.
 *
 * THERE IS NO "RECEIVED" MOVEMENT, and its absence is deliberate. Registering a
 * sample does not create a custody event claiming it arrived somewhere: the
 * arrival IS the registration, which is timestamped and attributed already.
 * Inventing a movement nobody recorded would be this system asserting a
 * physical fact it does not have (CLAUDE.md section 8).
 *
 * TWO TIMES ARE SHOWN, NOT ONE. When it happened, and when it was written down.
 * They are frequently different — somebody records a handover at the end of a
 * shift — and collapsing them into one would misstate both.
 */
export function SampleCustodyPanel({ sample }: { readonly sample: Sample }) {
  const { t } = useTranslation();
  const { can } = useAuth();
  const timeZone = useLaboratoryTimeZone();
  const { show } = useToast();
  const queryClient = useQueryClient();
  const locale = currentLocale();

  const [isOpen, setIsOpen] = useState(false);
  const [movementType, setMovementType] = useState<string | null>(null);
  const [occurredAt, setOccurredAt] = useState<string | null>(null);
  const [fromLocation, setFromLocation] = useState('');
  const [toLocation, setToLocation] = useState('');
  const [handedToName, setHandedToName] = useState('');
  const [note, setNote] = useState('');

  const movements = useQuery({
    queryKey: ['sample', sample.id, 'movements'],
    queryFn: () => samplesApi.movements(sample.id),
  });

  const record = useMutation({
    mutationFn: () =>
      samplesApi.recordMovement(sample.id, {
        movementType,
        // Absent means "now, as far as this record knows". An empty box is
        // never sent as a moment nobody stated.
        ...(occurredAt
          ? { occurredAt }
          : {}),
        ...(fromLocation.trim() ? { fromLocation: fromLocation.trim() } : {}),
        ...(toLocation.trim() ? { toLocation: toLocation.trim() } : {}),
        ...(handedToName.trim() ? { handedToName: handedToName.trim() } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sample', sample.id] });
      show({ tone: 'success', message: t('samples.movementRecorded') });
      setIsOpen(false);
      setMovementType(null);
      setOccurredAt(null);
      setFromLocation('');
      setToLocation('');
      setHandedToName('');
      setNote('');
    },
  });

  const needsNote =
    movementType !== null &&
    (MOVEMENTS_REQUIRING_NOTE as readonly string[]).includes(movementType);
  const isHandover = movementType === SAMPLE_MOVEMENT_TYPE.CUSTODY_HANDOVER;
  const mayRecord = can(PERMISSIONS.SAMPLE_STORAGE_MANAGE);

  return (
    <Panel
      title={t('samples.custodyTitle')}
      description={t('samples.custodyHint')}
      actions={
        mayRecord && !sample.isDisposed && !sample.isVoided ? (
          <ActionButton
            priority="secondary"
            size="sm"
            onPress={() => setIsOpen(true)}
          >
            {t('samples.recordMovement')}
          </ActionButton>
        ) : null
      }
    >
      {movements.isLoading ? (
        <p className="text-body text-default-600">{t('common.loading')}</p>
      ) : movements.error ? (
        // A failure to READ the chain is reported as a failure. It must never
        // look like a sample that has not moved.
        <p className="text-body text-warning-600">
          {t('samples.custodyUnavailable')}
        </p>
      ) : (movements.data ?? []).length === 0 ? (
        <p className="text-body text-default-600">{t('samples.noMovements')}</p>
      ) : (
        <ol className="flex flex-col gap-stack">
          {(movements.data ?? []).map((movement) => (
            <li
              key={movement.id}
              className="min-w-0 border-s-2 border-default-200 ps-3"
            >
              <StatusBadge
                label={t(`sampleMovement.${movement.movementType}`, {
                  defaultValue: movement.movementType,
                })}
                tone={toneForMovementType(movement.movementType)}
                size="sm"
              />
              {movement.fromLocation || movement.toLocation ? (
                <p className="mt-1 text-body text-default-800">
                  {t('samples.movementRoute', {
                    from: movement.fromLocation ?? t('common.notRecorded'),
                    to: movement.toLocation ?? t('common.notRecorded'),
                  })}
                </p>
              ) : null}
              {movement.handedToName ? (
                <p className="text-body text-default-800">
                  {t('samples.handedTo', { name: movement.handedToName })}
                </p>
              ) : null}
              {movement.note ? (
                <p className="text-body text-default-700">{movement.note}</p>
              ) : null}
              <p className="mt-1 text-caption text-default-600">
                {movement.recordedBy.displayName ?? t('common.notRecorded')}
              </p>
              {/* Both times, deliberately. */}
              <p className="text-caption text-default-600">
                {t('samples.occurredAtLine', {
                  moment: formatDateTime(movement.occurredAt, { locale }) ?? '',
                })}
              </p>
              <p className="text-caption text-default-600">
                {t('samples.recordedAtLine', {
                  moment: formatDateTime(movement.recordedAt, { locale }) ?? '',
                })}
              </p>
            </li>
          ))}
        </ol>
      )}

      <p className="mt-stack text-caption text-default-600">
        {t('samples.custodyAppendOnlyNotice')}
      </p>

      <Dialog
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={t('samples.recordMovement')}
        footer={
          <ActionBar>
            <ActionButton
              priority="tertiary"
              onPress={() => setIsOpen(false)}
              isDisabled={record.isPending}
            >
              {t('common.cancel')}
            </ActionButton>
            <ActionButton
              priority="primary"
              isDisabled={!movementType || (needsNote && !note.trim())}
              isLoading={record.isPending}
              onPress={() => record.mutate()}
            >
              {t('samples.recordMovement')}
            </ActionButton>
          </ActionBar>
        }
      >
        <div className="flex flex-col gap-stack">
          <SelectField
            label={t('samples.movementTypeLabel')}
            value={movementType}
            onChange={setMovementType}
            isRequired
            placeholder={t('samples.movementTypePlaceholder')}
            options={ALL_SAMPLE_MOVEMENT_TYPES.map((value) => ({
              value,
              label: t(`sampleMovement.${value}`),
              description: t(`sampleMovementHint.${value}`),
            }))}
          />
          <DateTimeField
            label={t('samples.occurredAtLabel')}
            description={t('samples.occurredAtHint')}
            value={occurredAt}
            onChange={setOccurredAt}
            timeZone={timeZone}
          />
          <TextField
            label={t('samples.fromLocation')}
            value={fromLocation}
            onChange={setFromLocation}
          />
          <TextField
            label={t('samples.toLocation')}
            value={toLocation}
            onChange={setToLocation}
          />
          {isHandover ? (
            <TextField
              label={t('samples.handedToLabel')}
              description={t('samples.handedToHint')}
              value={handedToName}
              onChange={setHandedToName}
            />
          ) : null}
          <TextAreaField
            label={t('samples.movementNote')}
            description={
              needsNote
                ? t('samples.movementNoteRequiredHint')
                : t('samples.movementNoteOptionalHint')
            }
            value={note}
            onChange={setNote}
            isRequired={needsNote}
            rows={3}
          />
          {record.error ? (
            <Alert tone="danger" title={t('samples.movementFailed')} />
          ) : null}
        </div>
      </Dialog>
    </Panel>
  );
}
